// The knowledge base (ERP-66). Every module ships its own articles beside its code
// (`src/help/*.md`), so a feature and its explanation change in the same merge request. An article
// is Markdown with a small front matter:
//
//   ---
//   title: Ending a contract early
//   summary: One line shown in lists and search results.
//   routes: /loans/:id, /loans/:id/edit     (pages the top bar's "?" opens this article on)
//   order: 30                                (position in the module's list; default 100)
//   keywords: termination, settlement        (extra words search should find it by)
//   ---
//
// Links inside an article are ordinary routes: `[the loan list](/loans)`, or another article:
// `[payouts](/help/loans/payouts)`.

export interface HelpArticle {
  /** Module key (`loans`), or `general` for the app's own articles. */
  module: string;
  /** File name without `.md`; with the module it makes the address `/help/<module>/<slug>`. */
  slug: string;
  title: string;
  summary: string;
  routes: string[];
  order: number;
  keywords: string[];
  body: string;
}

/** What a module's `help` loader resolves to: its section title and its raw articles by path. */
export interface HelpSource {
  title: string;
  articles: Record<string, string>;
}

export interface HelpSection {
  module: string;
  title: string;
  articles: HelpArticle[];
}

const FRONT = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;

/** One article from its file path (`./help/terminations.md`) and raw text. */
export function parseArticle(module: string, path: string, raw: string): HelpArticle {
  const slug = path.replace(/^.*\//, "").replace(/\.md$/, "");
  const m = raw.match(FRONT);
  const meta: Record<string, string> = {};
  for (const line of (m?.[1] ?? "").split(/\r?\n/)) {
    const i = line.indexOf(":");
    if (i > 0) meta[line.slice(0, i).trim()] = line.slice(i + 1).trim();
  }
  const list = (v?: string) =>
    (v ?? "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  const body = m ? raw.slice(m[0].length) : raw;
  return {
    module,
    slug,
    title: meta.title || body.match(/^#\s+(.+)$/m)?.[1] || slug,
    summary: meta.summary ?? "",
    routes: list(meta.routes),
    order: Number(meta.order) || 100,
    keywords: list(meta.keywords).map((k) => k.toLowerCase()),
    body: body.trim(),
  };
}

export function helpSection(module: string, src: HelpSource): HelpSection {
  return {
    module,
    title: src.title,
    articles: Object.entries(src.articles)
      .map(([path, raw]) => parseArticle(module, path, raw))
      .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title)),
  };
}

/** Does `/loans/:id` (or `/settings/*`) describe the path `/loans/42`? */
export function routeMatches(pattern: string, path: string): boolean {
  const p = pattern.replace(/\/+$/, "").split("/");
  const s = (path.split(/[?#]/)[0] ?? "").replace(/\/+$/, "").split("/");
  for (let i = 0; i < p.length; i++) {
    const want = p[i] ?? "";
    if (want === "*") return true;
    if (i >= s.length) return false;
    if (want.startsWith(":")) {
      if (!s[i]) return false;
    } else if (want !== s[i]) return false;
  }
  return p.length === s.length;
}

/**
 * The article to open on a page: the one whose route matches most exactly — literal segments count
 * more than parameters, so `/loans/new` beats `/loans/:id` on the new-loan page.
 */
export function articleFor(sections: HelpSection[], path: string): HelpArticle | undefined {
  let best: { a: HelpArticle; score: number } | undefined;
  for (const s of sections)
    for (const a of s.articles)
      for (const r of a.routes) {
        if (!routeMatches(r, path)) continue;
        const parts = r.split("/").filter(Boolean);
        const score =
          parts.reduce((n, x) => n + (x === "*" ? 0 : x.startsWith(":") ? 1 : 3), 0) - a.order / 1000;
        if (!best || score > best.score) best = { a, score };
      }
  return best?.a;
}

/** Plain text of an article for searching: no Markdown punctuation. */
const plain = (md: string) =>
  md
    .replace(/`[^`]*`/g, (c) => c.slice(1, -1))
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[#>*_|-]+/g, " ")
    .toLowerCase();

export interface HelpHit {
  article: HelpArticle;
  section: string;
  /** A sentence around the first match, for the results list. */
  excerpt: string;
}

/** Articles containing every word of the query, best first: title, then keywords, then body. */
export function searchHelp(sections: HelpSection[], query: string): HelpHit[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const hits: (HelpHit & { score: number })[] = [];
  for (const s of sections)
    for (const a of s.articles) {
      const title = a.title.toLowerCase();
      const head = `${title} ${a.summary.toLowerCase()} ${a.keywords.join(" ")}`;
      const text = plain(a.body);
      if (!words.every((w) => head.includes(w) || text.includes(w))) continue;
      const score = words.reduce(
        (n, w) => n + (title.includes(w) ? 10 : 0) + (head.includes(w) ? 4 : 0) + (text.includes(w) ? 1 : 0),
        0,
      );
      const at = text.indexOf(words[0] ?? "");
      const excerpt =
        at < 0
          ? a.summary
          : `${at > 60 ? "…" : ""}${text
              .slice(Math.max(0, at - 60), at + 120)
              .replace(/\s+/g, " ")
              .trim()}…`;
      hits.push({ article: a, section: s.title, excerpt, score });
    }
  return hits.sort((x, y) => y.score - x.score).map(({ score: _, ...h }) => h);
}
